import { ROUTES } from "@/lib/routes";

/**
 * Páginas que NÃO devem ser usadas como destino de retorno após login.
 * Caem no fluxo padrão (resolveRedirectByRole).
 */
const NON_RETURNABLE_PATHS = [
  ROUTES.AUTH,
  "/selecionar-perfil",
  "/404",
  "/error",
];

function isReturnable(path: string): boolean {
  if (!path || !path.startsWith("/")) return false;
  if (path.startsWith("//")) return false; // protege contra protocol-relative
  return !NON_RETURNABLE_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

/* ------------------------------------------------------------------ */
/* Tournament return context (sessionStorage fallback)                 */
/* ------------------------------------------------------------------ */

const SS_RETURN_URL = "tournament:returnUrl";
const SS_LAST_SLUG = "tournament:lastSlug";
const SS_LAST_CATEGORY = "tournament:lastCategoryId";

/**
 * Detecta se um path interno pertence ao fluxo público de torneios
 * (página do torneio, inscrição autenticada, inscrição sem login,
 * confirmação de inscrição compartilhada).
 */
export function isTournamentPath(path: string): boolean {
  if (!path) return false;
  return (
    path.startsWith("/torneios/") ||
    path.startsWith("/inscricao-confirmacao/") ||
    path.startsWith("/completar-inscricao/")
  );
}

/**
 * Extrai o slug do torneio a partir de um path como
 *   /torneios/:slug
 *   /torneios/:slug/inscricao
 *   /torneios/:slug/chaves
 */
export function extractTournamentSlug(path: string): string | null {
  const m = path.match(/^\/torneios\/([^/?#]+)/);
  return m ? m[1] : null;
}

/**
 * Persiste no sessionStorage o contexto de retorno para o torneio,
 * para sobreviver ao redirect de confirmação de email (que pode descartar
 * a query string original).
 */
export function saveTournamentReturnContext(opts?: {
  url?: string;
  slug?: string | null;
  categoryId?: string | null;
}) {
  if (typeof window === "undefined") return;
  try {
    const url = opts?.url ?? `${window.location.pathname}${window.location.search}`;
    if (!isTournamentPath(url)) return;
    sessionStorage.setItem(SS_RETURN_URL, url);
    const slug = opts?.slug ?? extractTournamentSlug(url);
    if (slug) sessionStorage.setItem(SS_LAST_SLUG, slug);
    const params = new URLSearchParams(url.split("?")[1] || "");
    const cat =
      opts?.categoryId ??
      params.get("category") ??
      params.get("cat") ??
      params.get("categoryId");
    if (cat) sessionStorage.setItem(SS_LAST_CATEGORY, cat);
  } catch {
    /* sessionStorage indisponível (modo privado, etc.) */
  }
}

export function loadTournamentReturnContext(): {
  returnUrl: string | null;
  lastSlug: string | null;
  lastCategoryId: string | null;
} {
  if (typeof window === "undefined") {
    return { returnUrl: null, lastSlug: null, lastCategoryId: null };
  }
  try {
    return {
      returnUrl: sessionStorage.getItem(SS_RETURN_URL),
      lastSlug: sessionStorage.getItem(SS_LAST_SLUG),
      lastCategoryId: sessionStorage.getItem(SS_LAST_CATEGORY),
    };
  } catch {
    return { returnUrl: null, lastSlug: null, lastCategoryId: null };
  }
}

export function clearTournamentReturnContext() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SS_RETURN_URL);
    sessionStorage.removeItem(SS_LAST_SLUG);
    sessionStorage.removeItem(SS_LAST_CATEGORY);
  } catch {
    /* noop */
  }
}

/**
 * Retorna o returnUrl do torneio salvo (validado), ou null.
 * Use como fallback quando a query string `returnUrl` não chegou na página
 * de auth (ex.: depois de clicar no link do email de confirmação).
 */
export function getStoredTournamentReturnUrl(): string | null {
  const { returnUrl } = loadTournamentReturnContext();
  if (!returnUrl) return null;
  const sanitized = sanitizeReturnUrl(returnUrl);
  if (!sanitized || !isTournamentPath(sanitized)) return null;
  return sanitized;
}

/* ------------------------------------------------------------------ */
/* Auth URL helpers                                                    */
/* ------------------------------------------------------------------ */

/**
 * Monta a URL de autenticação preservando a página atual como returnUrl.
 * Use sempre que enviar o usuário para login/cadastro a partir de qualquer página.
 *
 * Para páginas de torneio, também persiste o contexto em sessionStorage
 * (tournament:returnUrl, tournament:lastSlug, tournament:lastCategoryId)
 * como fallback caso a query string seja perdida no caminho.
 *
 * @param tab 'signin' (default) ou 'signup' para abrir já na aba de cadastro
 */
export function buildAuthUrl(tab: "signin" | "signup" = "signin"): string {
  const current =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "";
  const params = new URLSearchParams();
  if (isReturnable(current)) {
    params.set("returnUrl", current);
    if (isTournamentPath(current)) {
      saveTournamentReturnContext({ url: current });
    }
  }
  if (tab === "signup") {
    params.set("tab", "cadastro");
  }
  const qs = params.toString();
  return qs ? `${ROUTES.AUTH}?${qs}` : ROUTES.AUTH;
}

/**
 * Valida se um returnUrl recebido por query string pode ser usado com segurança
 * (path interno relativo, não URL absoluta externa).
 */
export function sanitizeReturnUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}
